import { CheckOutlined, CloseOutlined, PaperClipOutlined } from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, Tag } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../components/ActionTooltip.tsx"
import {
  buildEditRowCells,
  rowCommitKeyDown
} from "../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import InputNumber from "../../../components/Input/InputNumber.tsx"
import { TextArea } from "../../../components/Input/TextArea.tsx"
import { unavailableProps } from "../../../utils/unavailable-control.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../utils/object/is-form-changed.ts"
import { invoiceFormFieldErrors } from "../helpers/handle-invoice-api-error.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import {
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import { buildInvoiceEditPatch } from "../helpers/invoice-form.utils.ts"
import { ProjectViewInvoiceAssigneeSelect } from "../../Project/ProjectViewPage/components/sections/ProjectViewInvoices/ProjectViewInvoices/ProjectViewInvoiceAssigneeSelect.tsx"
import { INVOICE_COLUMN_KEYS } from "../../Project/ProjectViewPage/components/sections/ProjectViewInvoices/ProjectViewInvoices/columns.tsx"

interface InvoiceEditRowProps {
  // rc-table hands the default cells to a body-row component; the edit row
  // matches its own cells against them by column key.
  children?: ReactNode
  record: ProjectInvoice
  options: Record<string, BaseOptionType[]>
  selfAssigned: boolean
  /** The table's configurable column keys; defaults to the project tab's. */
  columnKeys?: readonly string[]
  onSelectRow: (record: ProjectInvoice, action: string) => Promise<void> | void
}

const EMPTY_LINES: Record<string, number> = {}

/**
 * Inline correction of the fields an invoice row shows. Only what the user
 * moved is sent, so an untouched date is never echoed back in the account's
 * display format and `lines` is never resent.
 */
export const InvoiceEditRow = ({
  record,
  onSelectRow,
  options,
  selfAssigned,
  columnKeys = Object.values(INVOICE_COLUMN_KEYS),
  ...rest
}: InvoiceEditRowProps) => {
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()
  const { t } = useTranslation()
  const saveLabel = t("invoices.row.save")

  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectInvoice>()
  const { modal } = App.useApp()

  const handleSave = async () => {
    let values: ProjectInvoice
    try {
      values = await validateFormWithScroll(form)
    } catch {
      return
    }

    const patch = buildInvoiceEditPatch(
      values,
      record,
      normalizeDate,
      EMPTY_LINES,
      EMPTY_LINES
    )
    if (!Object.keys(patch).length) {
      onSelectRow(record, "cancel")
      return
    }

    try {
      setSaving(true)
      await onSelectRow({ id: record.id, ...patch } as ProjectInvoice, "update")
    } catch (error) {
      const fieldErrors = invoiceFormFieldErrors(error)
      if (fieldErrors.length) {
        form.setFields(
          fieldErrors.map(({ name, errors }) => ({
            name: name as keyof ProjectInvoice,
            errors
          }))
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const isChanged = isFormChanged<ProjectInvoice>(
      form.getFieldsValue(),
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, "cancel")
        }
      })
    } else {
      onSelectRow(record, "cancel")
    }
  }

  // Status is derived from payments server-side; the row only reports it.
  const statusDisplay = record?.status
    ? INVOICE_STATUSES_MAP[record.status as InvoiceStatuses]
    : undefined

  const commitOnEnter = rowCommitKeyDown(() => void handleSave(), {
    busy: saving
  })
  const onKeyDownRow: React.KeyboardEventHandler<HTMLTableRowElement> = (
    event
  ) => {
    if (event.key === "Escape" && !saving) {
      // An open picker or select closes itself on Escape; only a bare Escape
      // discards the row.
      const target = event.target as HTMLElement
      if (target?.closest?.(".ant-select-open, .ant-picker-focused")) return
      event.preventDefault()
      handleCancel()
      return
    }
    commitOnEnter(event)
  }

  // Not `noStyle`: a server refusal has to read as text under its own cell.
  const cellItem = "m-0"

  return (
    <Form<ProjectInvoice>
      form={form}
      component={false}
      initialValues={record}
      disabled={saving}
    >
      <FormChangedWatcher<ProjectInvoice> form={form} saving={saving} />

      <tr {...rest} onKeyDown={onKeyDownRow} data-testid="invoice-edit-row">
        {buildEditRowCells(
          rest?.children,
          columnKeys,
          {
            [INVOICE_COLUMN_KEYS.invoiceNumber]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice>
                  name="invoice_number"
                  className={cellItem}
                  rules={[
                    {
                      required: true,
                      message: t("finance.quickAddInvoice.validation.required")
                    },
                    {
                      max: 50,
                      message: t(
                        "finance.quickAddInvoice.validation.invoiceNumberMax"
                      )
                    }
                  ]}
                >
                  <TextArea
                    disableAutoCapitalize
                    autoSize
                    aria-label={t("invoices.columns.invoiceNumber")}
                    placeholder={t("invoices.columns.invoiceNumber")}
                    autoFocus
                  />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.customerName]: (
              <td className="ant-table-cell">{record?.customer_name || "—"}</td>
            ),
            [INVOICE_COLUMN_KEYS.dateIssued]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice>
                  name="date_issued"
                  className={cellItem}
                  getValueProps={getDateValueProps}
                  rules={[
                    {
                      required: true,
                      message: t("finance.quickAddInvoice.validation.required")
                    },
                    ...createDateRangeRules<ProjectInvoice>("due_date", "start")
                  ]}
                  dependencies={["due_date"]}
                >
                  <DatePicker
                    className="w-full"
                    aria-label={t("invoices.columns.dateIssued")}
                    onChange={dateAutoFill<ProjectInvoice>({
                      form,
                      endDatePath: ["due_date"]
                    })}
                  />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.dueDate]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice>
                  name="due_date"
                  className={cellItem}
                  getValueProps={getDateValueProps}
                  rules={[
                    {
                      required: true,
                      message: t("finance.quickAddInvoice.validation.required")
                    },
                    ...createDateRangeRules<ProjectInvoice>(
                      "date_issued",
                      "end"
                    )
                  ]}
                  dependencies={["date_issued"]}
                >
                  <DatePicker
                    className="w-full"
                    aria-label={t("invoices.columns.dueDate")}
                  />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.amount]: (
              <td className="ant-table-cell">
                <Form.Item<ProjectInvoice>
                  name="amount"
                  className={cellItem}
                  rules={[
                    {
                      required: true,
                      message: t("finance.quickAddInvoice.validation.required")
                    }
                  ]}
                >
                  <InputNumber
                    min={0}
                    className="w-full"
                    prefix="$"
                    placeholder="0.00"
                    aria-label={t("invoices.columns.amount")}
                  />
                </Form.Item>
              </td>
            ),
            [INVOICE_COLUMN_KEYS.status]: (
              <td className="ant-table-cell">
                {statusDisplay ? (
                  <Tag style={statusDisplay.style} className="m-0">
                    {record?.status_display || statusDisplay.label}
                  </Tag>
                ) : (
                  "—"
                )}
              </td>
            ),
            [INVOICE_COLUMN_KEYS.assigneeName]: (
              <td className="ant-table-cell">
                <ProjectViewInvoiceAssigneeSelect
                  form={form}
                  disabled={selfAssigned}
                  options={options}
                />
              </td>
            ),
            [INVOICE_COLUMN_KEYS.attachments]: (
              <td className="ant-table-cell text-center">
                <ActionTooltip title={t("invoices.row.attachmentsViewOnly")}>
                  <Button
                    aria-label={t("invoices.row.attachmentsViewOnly")}
                    {...unavailableProps(true)}
                    icon={<PaperClipOutlined />}
                  />
                </ActionTooltip>
              </td>
            )
          },
          <td className="sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first bg-white">
            <Flex gap="small" justify="center">
              <ActionTooltip title={t("invoices.row.cancel")}>
                <Button
                  aria-label={t("invoices.row.cancel")}
                  variant="outlined"
                  color="danger"
                  onClick={handleCancel}
                  icon={<CloseOutlined />}
                />
              </ActionTooltip>
              <ActionTooltip title={saveLabel}>
                <Button
                  aria-label={saveLabel}
                  loading={saving}
                  variant="outlined"
                  color="green"
                  onClick={handleSave}
                  icon={<CheckOutlined />}
                />
              </ActionTooltip>
            </Flex>
          </td>
        )}
      </tr>
    </Form>
  )
}
